/**
 * "Monthly performance" chart for the Properties page. Mirrors the
 * `.perf-chart-section` from `design/Syntra/properties.html`:
 *
 *   - Header (title + dynamic subtitle) with Strategy + Property filters.
 *   - Legend (Rent · Monthly expenses · Monthly cash flow).
 *   - A 12-month trailing line/area chart of rent vs. expenses vs. cash flow.
 *
 * The series come from the pure `buildMonthlySeries` calculator so the latest
 * month lines up with the strategy-aware cash flow shown on each card.
 */
import { useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Strategy } from "@/types/pipeline";

import {
  aggregatePerformanceSeries,
  type MonthlyResult,
} from "./cashflow-metrics";

const ALL = "all";

const SERIES = [
  { key: "rent", name: "Rent", color: "hsl(var(--success))" },
  { key: "expenses", name: "Monthly expenses", color: "hsl(var(--destructive))" },
  { key: "cashflow", name: "Monthly cash flow", color: "hsl(217 91% 60%)" },
] as const;

function fmtMoneyShort(n: number): string {
  const v = Math.round(n || 0);
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1000) {
    const k = a / 1000;
    return `${sign}$${k >= 10 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `${sign}$${a}`;
}

export interface PerformanceEntry {
  id: string;
  label: string;
  strategy: Strategy;
  /** Real trailing monthly series (frozen past + live current, no future). */
  months: MonthlyResult[];
}

interface PropertiesPerformanceChartProps {
  entries: PerformanceEntry[];
}

export function PropertiesPerformanceChart({
  entries,
}: PropertiesPerformanceChartProps) {
  const [strategy, setStrategy] = useState<string>(ALL);
  const [propertyId, setPropertyId] = useState<string>(ALL);

  const strategies = useMemo(
    () => [...new Set(entries.map((e) => e.strategy))],
    [entries],
  );

  // Property options narrow when a strategy filter is active.
  const propertyPool = useMemo(
    () =>
      strategy === ALL
        ? entries
        : entries.filter((e) => e.strategy === strategy),
    [entries, strategy],
  );

  // Drop a stale property pick if it no longer matches the pool.
  const activePropertyId =
    propertyId !== ALL && !propertyPool.some((e) => e.id === propertyId)
      ? ALL
      : propertyId;

  const filtered = useMemo(
    () =>
      activePropertyId === ALL
        ? propertyPool
        : propertyPool.filter((e) => e.id === activePropertyId),
    [propertyPool, activePropertyId],
  );

  const data = useMemo(
    () => aggregatePerformanceSeries(filtered.map((e) => e.months)),
    [filtered],
  );

  if (entries.length === 0) return null;

  const scope =
    activePropertyId === ALL
      ? filtered.length === 1
        ? "1 property"
        : `${filtered.length} properties combined`
      : (filtered[0]?.label ?? "Property");

  return (
    <Card className="shadow-soft p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-bold text-foreground">
            Monthly performance
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Last 12 months · {scope}
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Select
            value={strategy}
            onValueChange={(v) => {
              setStrategy(v);
              setPropertyId(ALL); // strategy change resets the property pick
            }}
          >
            <SelectTrigger className="h-9 w-[150px] text-xs" aria-label="Filter by strategy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All strategies</SelectItem>
              {strategies.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={activePropertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="h-9 w-[170px] text-xs" aria-label="Filter by property">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All properties</SelectItem>
              {propertyPool.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`perf-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 4" className="stroke-border" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={fmtMoneyShort}
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "12px",
              }}
              formatter={(value: number, name: string) => [fmtMoneyShort(value), name]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2.25}
                fill={`url(#perf-${s.key})`}
                dot={false}
                activeDot={{ r: 3 }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
